import { SeloDeMaquina } from '../../../components/SeloDeMaquina'
import { SeveridadeDaIa } from '../../../components/SeveridadeDaIa'
import { TEXTOS } from '../../../lib/glossario'
import type { AlertaDaIa } from '../../../types/chamado'
import type { Discordancia, ParecerDaCompetencia } from '../../../types/apoioDaIa'
import { DiscordarDoParecer } from './DiscordarDoParecer'

/**
 * O texto do parecer do mês: o resumo, os pontos a conferir e a procedência.
 *
 * **Nenhum número desta tela sai daqui.** Os indicadores, o desconto e as contagens vêm da apuração
 * do sistema, acima; isto é texto sobre o que conferir. O servidor recusa o parecer inteiro quando
 * ele cita um percentual que não estava no recorte ou qualquer valor em reais, e o selo repete o
 * limite para quem lê.
 *
 * **`NADA_A_CONFERIR` não é aprovação, e a tela escreve isso.** É o momento em que a máquina mais
 * facilmente passaria por autorização: um texto tranquilo ao lado de um botão de fechar. A frase do
 * glossário diz, com todas as letras, que quem confere e assina é o fiscal.
 *
 * @param props a competência, o parecer e se o leitor pode registrar discordância
 * @returns o corpo do parecer
 */
export function ParecerDoMes({
  competencia,
  parecer,
  podeDiscordar,
}: {
  competencia: string
  parecer: ParecerDaCompetencia
  podeDiscordar: boolean
}) {
  const t = TEXTOS.conferenciaDoMes

  if (parecer.situacao === 'FALHOU') {
    return <p className="text-sm text-slate-600">{parecer.erro ?? t.falhou}</p>
  }

  const doParecerInteiro = parecer.discordancias.filter((d) => d.itemCodigo === null)

  return (
    <div className="space-y-3">
      {parecer.resumo && <p className="text-sm leading-relaxed text-slate-700">{parecer.resumo}</p>}

      {parecer.itens.length === 0 ? (
        <p className="text-sm text-slate-600">{t.nadaAConferir}</p>
      ) : (
        <div>
          <h3 className="text-sm font-medium text-slate-700">{t.itens}</h3>
          <ul className="mt-2 space-y-2">
            {parecer.itens.map((item, indice) => (
              <Item
                key={`${item.codigo}-${indice}`}
                competencia={competencia}
                parecerId={parecer.id}
                item={item}
                discordancias={parecer.discordancias.filter((d) => d.itemCodigo === item.codigo)}
                podeDiscordar={podeDiscordar}
              />
            ))}
          </ul>
        </div>
      )}

      {podeDiscordar && parecer.itens.length === 0 && (
        <DiscordarDoParecer
          competencia={competencia}
          parecerId={parecer.id}
          itemCodigo={null}
          discordancias={doParecerInteiro}
        />
      )}

      <SeloDeMaquina modelo={parecer.modelo} quando={parecer.concluidoEm} />
    </div>
  )
}

/**
 * Um ponto a conferir.
 *
 * O rótulo em pt-BR vem do glossário; código sem rótulo cai no próprio código, e não some — um
 * fiscal leigo lendo `CORRETIVO_SEM_CRITICIDADE` é ruim, mas ler nada é pior.
 *
 * @param props o item, as discordâncias dele e o que a pessoa pode fazer
 * @returns a linha do item
 */
function Item({
  competencia,
  parecerId,
  item,
  discordancias,
  podeDiscordar,
}: {
  competencia: string
  parecerId: number
  item: AlertaDaIa
  discordancias: Discordancia[]
  podeDiscordar: boolean
}) {
  const t = TEXTOS.conferenciaDoMes

  return (
    <li className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <SeveridadeDaIa severidade={item.severidade} />
        <span className="text-sm font-medium text-slate-800">
          {item.titulo ?? t.codigos[item.codigo] ?? item.codigo}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-slate-500">{t.codigos[item.codigo] ?? item.codigo}</p>
      {item.explicacao && <p className="mt-1 text-sm text-slate-600">{item.explicacao}</p>}
      {item.verificacao && (
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-medium">{t.comoConferir}</span> {item.verificacao}
        </p>
      )}
      {podeDiscordar && (
        <DiscordarDoParecer
          competencia={competencia}
          parecerId={parecerId}
          itemCodigo={item.codigo}
          discordancias={discordancias}
        />
      )}
    </li>
  )
}

